import React, { memo, useMemo } from "react";
import { Handle, Position } from "@xyflow/react";
import NodeWrapper from "./NodeWrapper";
import useHandleConnected from "./useHandleConnected";
import {
  LibraryBooks,
  Message,
  AccessTime,
  Image,
  MicNone,
  Videocam,
  Description,
} from "@mui/icons-material";

const ICON_MAP = {
  message: { Icon: Message, label: "Mensagem", color: "#4CAF50" },
  interval: { Icon: AccessTime, label: "Intervalo", color: "#F7953B" },
  img: { Icon: Image, label: "Imagem", color: "#2196F3" },
  audio: { Icon: MicNone, label: "Audio", color: "#9C27B0" },
  video: { Icon: Videocam, label: "Video", color: "#E91E63" },
  document: { Icon: Description, label: "Documento", color: "#795548" },
};

function getSingleBlockHandles(data) {
  const handles = [];
  const elements = data?.elements || [];
  elements.forEach((el) => {
    if (el?.type === "button") {
      const opts = el.arrayOption || el.array_option || [];
      opts.forEach((o, idx) => {
        if (o?.value)
          handles.push({ id: `btn_${idx + 1}`, label: o.value, hType: "button" });
      });
    }
    if (el?.type === "list") {
      const sections = el.sections || [];
      (sections[0]?.rows || []).forEach((row, idx) => {
        const rowId =
          row?.id != null && String(row.id).trim() !== ""
            ? String(row.id) : String(idx + 1);
        handles.push({
          id: `lst_${rowId}`,
          label: row?.title || row?.id || `Item ${idx + 1}`,
          hType: "list",
        });
      });
    }
  });
  return handles;
}

const SingleBlockNode = memo(({ id, data, isConnectable, selected }) => {
  const getHandleClass = useHandleConnected(id);
  const optionHandles = useMemo(() => getSingleBlockHandles(data), [data]);
  const seq = data?.seq || [];
  const elements = data?.elements || [];

  return (
    <NodeWrapper
      id={id}
      data={data}
      icon={<LibraryBooks />}
      title="Conteudo"
      color="#EC5858"
      bgColor="#FEFAFA"
      noSource={optionHandles.length > 0}
      isConnectable={isConnectable}
      selected={selected}
      minWidth={220}
    >
      {/* Content elements preview */}
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {seq.map((item, index) => {
          const typeKey = Object.keys(ICON_MAP).find((k) => item.includes(k));
          const entry = typeKey
            ? ICON_MAP[typeKey]
            : { Icon: Message, label: "Elemento", color: "#999" };
          const { Icon, label, color } = entry;
          const el = elements.find((e) => e.number === item);
          const preview =
            typeKey === "message" || typeKey === "interval"
              ? el?.value || "" : el?.original || el?.value || "";

          return (
            <div
              key={index}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "#fff", border: "1px solid #f0e0e0",
                borderRadius: 6, padding: "5px 8px",
                borderLeft: `3px solid ${color}`,
              }}
            >
              <Icon style={{ fontSize: 14, color, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: "#555", fontWeight: 500, flexShrink: 0 }}>
                {label}
              </span>
              {preview && (
                <span style={{
                  fontSize: 10, color: "#888",
                  overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap", flex: 1, maxWidth: 120,
                }}>
                  {preview}{typeKey === "interval" ? "s" : ""}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Interactive element labels with handles at node edge */}
      {optionHandles.length > 0 && (
        <div style={{
          marginTop: 6, borderTop: "1px solid #f0e0e0",
          paddingTop: 6, display: "flex", flexDirection: "column", gap: 4,
        }}>
          {optionHandles.map((h) => (
            <div
              key={h.id}
              style={{
                display: "flex", alignItems: "center",
                justifyContent: "flex-end", gap: 4,
                fontSize: 10, position: "relative",
              }}
            >
              <span style={{
                background: h.hType === "list" ? "#E3F2FD" : "#FFEBEE",
                color: h.hType === "list" ? "#1976d2" : "#EC5858",
                borderRadius: 4, padding: "1px 6px",
                fontWeight: 600, fontSize: 9,
                textTransform: "uppercase", letterSpacing: "0.03em",
              }}>
                {h.hType === "list" ? "lista" : "btn"}
              </span>
              <span style={{
                color: "#666", overflow: "hidden", textOverflow: "ellipsis",
                whiteSpace: "nowrap", maxWidth: 100,
              }}>
                {h.label}
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id={h.id}
                className={getHandleClass("source", h.id)}
                style={{
                  position: "absolute",
                  top: "50%",
                  right: -13,
                  transform: "translateY(-50%)",
                  cursor: "crosshair",
                }}
                isConnectable={isConnectable}
              />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {seq.length === 0 && optionHandles.length === 0 && (
        <div style={{ fontSize: 11, color: "#999", fontStyle: "italic", padding: "4px 0" }}>
          Bloco vazio
        </div>
      )}
    </NodeWrapper>
  );
});

SingleBlockNode.displayName = "SingleBlockNode";
export default SingleBlockNode;
